import { useEffect, useRef } from 'react';
import type { Conversation, Message } from '../types';

interface Props {
  conversation:   Conversation;
  messages:       Message[];
  loadingMsgs:    boolean;
  linkedClient:   { id: string; full_name: string } | null;
  onMarkClosed:   () => void;
  onConvert:      () => void;
  onLinkClient:   () => void;
  onBookAppt:     () => void;
  converting:     boolean;
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/* ── Shared action button style ── */
function ActionBtn({
  icon, label, onClick, disabled, variant = 'default', done = false,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'default' | 'gold' | 'danger';
  done?: boolean;
}) {
  const base =
    'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-xs font-medium transition-all cursor-pointer whitespace-nowrap';
  const styles = {
    default: done
      ? 'bg-emerald-50 border-emerald-200 text-emerald-600 cursor-default'
      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300',
    gold: 'text-white border-transparent hover:opacity-90',
    danger: 'bg-white border-slate-200 text-red-500 hover:bg-red-50 hover:border-red-200',
  };

  return (
    <button
      type="button"
      onClick={!done ? onClick : undefined}
      disabled={disabled}
      className={`${base} ${styles[variant]} disabled:opacity-50`}
      style={variant === 'gold' ? { backgroundColor: '#C8A96E' } : undefined}
    >
      {disabled ? (
        <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin shrink-0" />
      ) : (
        <i className={`${icon} text-xs shrink-0`} />
      )}
      <span className="flex-1 text-left">{label}</span>
      {done && <i className="ri-check-line text-emerald-500 text-xs shrink-0" />}
    </button>
  );
}

export default function ConversationDetail({
  conversation, messages, loadingMsgs,
  linkedClient, onMarkClosed, onConvert, onLinkClient, onBookAppt,
  converting,
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const isClosed = conversation.status === 'closed';
  const hasLead  = !!conversation.lead_id;

  return (
    <div className="flex flex-col h-full">

      {/* ── Header ── */}
      <div className="px-5 py-3.5 border-b border-slate-200 bg-white shrink-0">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 flex items-center justify-center rounded-full text-white font-bold text-sm shrink-0"
            style={{ backgroundColor: '#C8A96E' }}
          >
            {(conversation.visitor_name ?? 'V')[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[#0f172a] font-semibold text-sm truncate">
              {conversation.visitor_name ?? 'Anonymous Visitor'}
            </p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  isClosed
                    ? 'bg-slate-100 text-slate-500'
                    : 'bg-emerald-100 text-emerald-600'
                }`}
              >
                {conversation.status}
              </span>
              <span className="text-slate-400 text-[10px]">
                {formatTime(conversation.created_at)}
              </span>
              <span className="text-slate-400 text-[10px]">via {conversation.channel}</span>
              {linkedClient && (
                <span className="flex items-center gap-1 text-[10px] text-[#C8A96E] font-medium">
                  <i className="ri-user-3-line" />
                  {linkedClient.full_name}
                </span>
              )}
            </div>
          </div>

          {hasLead && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 whitespace-nowrap shrink-0">
              <i className="ri-user-received-line text-[11px]" />
              Lead created
            </span>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 flex min-h-0">

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 bg-slate-50 space-y-3">
          {loadingMsgs ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`h-10 bg-slate-200 rounded-2xl animate-pulse ${
                    i % 2 === 0 ? 'ml-16' : 'mr-16'
                  }`}
                />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <i className="ri-chat-1-line text-slate-200 text-3xl mb-2" />
              <p className="text-slate-400 text-xs">No messages recorded</p>
              <p className="text-slate-300 text-[11px] mt-1">
                Messages are saved when the visitor interacts with the widget.
              </p>
            </div>
          ) : (
            messages.map((msg) => {
              const isBot = msg.role === 'assistant';
              return (
                <div
                  key={msg.id}
                  className={`flex items-end gap-2 ${isBot ? '' : 'flex-row-reverse'}`}
                >
                  {isBot && (
                    <div
                      className="w-6 h-6 flex items-center justify-center rounded-full shrink-0"
                      style={{ backgroundColor: 'rgba(200,169,110,0.2)' }}
                    >
                      <i className="ri-robot-line text-[#C8A96E] text-[10px]" />
                    </div>
                  )}
                  <div
                    className={`max-w-[72%] px-3.5 py-2.5 rounded-2xl ${
                      isBot
                        ? 'bg-white rounded-bl-sm border border-slate-200'
                        : 'rounded-br-sm text-white'
                    }`}
                    style={!isBot ? { backgroundColor: '#C8A96E' } : undefined}
                  >
                    <p
                      className={`text-[11px] leading-relaxed ${
                        isBot ? 'text-[#0f172a]' : 'text-white'
                      }`}
                    >
                      {msg.content}
                    </p>
                    <p
                      className={`text-[9px] mt-1 text-right ${
                        isBot ? 'text-slate-300' : 'text-white/60'
                      }`}
                    >
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Right panel: visitor info + CRM actions */}
        <div className="w-52 shrink-0 border-l border-slate-200 bg-white overflow-y-auto flex flex-col">

          {/* Visitor info */}
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-slate-400 text-[10px] uppercase tracking-wider font-semibold mb-2.5">
              Visitor Info
            </p>
            <div className="space-y-2.5">
              {[
                { label: 'Email',  value: conversation.visitor_email, icon: 'ri-mail-line' },
                { label: 'Phone',  value: conversation.visitor_phone, icon: 'ri-phone-line' },
                { label: 'Event',  value: conversation.event_type,    icon: 'ri-calendar-line' },
                { label: 'Date',   value: conversation.event_date,    icon: 'ri-calendar-2-line' },
              ].map((item) => (
                <div key={item.label}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <i className={`${item.icon} text-slate-300 text-[10px]`} />
                    <p className="text-slate-400 text-[10px] uppercase tracking-wider">
                      {item.label}
                    </p>
                  </div>
                  <p
                    className={`text-xs break-all ${
                      item.value ? 'text-[#0f172a]' : 'text-slate-300 italic'
                    }`}
                  >
                    {item.value ?? '—'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CRM Actions */}
          <div className="px-3 py-3 space-y-2 flex-1">
            <p className="text-slate-400 text-[10px] uppercase tracking-wider font-semibold px-1 mb-3">
              CRM Actions
            </p>

            {/* Convert to Lead */}
            <ActionBtn
              icon="ri-user-received-line"
              label={hasLead ? 'Converted to Lead' : 'Convert to Lead'}
              onClick={onConvert}
              disabled={converting}
              variant="gold"
              done={hasLead}
            />

            {/* Link Client */}
            <ActionBtn
              icon="ri-user-3-line"
              label={linkedClient ? `Linked: ${linkedClient.full_name.split(' ')[0]}` : 'Link to Client'}
              onClick={onLinkClient}
              done={!!linkedClient}
            />

            {/* Book Appointment */}
            <ActionBtn
              icon="ri-calendar-event-line"
              label="Create Appointment"
              onClick={onBookAppt}
            />

            {/* Close */}
            {!isClosed && (
              <ActionBtn
                icon="ri-checkbox-circle-line"
                label="Mark as Closed"
                onClick={onMarkClosed}
                variant="danger"
              />
            )}

            {isClosed && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
                <i className="ri-check-double-line text-slate-400 text-xs" />
                <span className="text-slate-400 text-xs">Conversation closed</span>
              </div>
            )}
          </div>

          {/* Conv ID footer */}
          <div className="px-4 py-3 border-t border-slate-100">
            <p className="text-slate-300 text-[10px] uppercase tracking-wider mb-0.5">ID</p>
            <p className="text-[10px] text-slate-300 font-mono">{conversation.id.slice(0, 8)}…</p>
          </div>
        </div>
      </div>
    </div>
  );
}